/**
 * ECS World — owns entities and their component stores.
 *
 * Determinism: entity ids are strictly increasing (never recycled in a way that
 * reorders), and every query returns entities in ascending-id order regardless of
 * internal store churn. This makes system execution order reproducible, which is
 * the bedrock of the deterministic simulation.
 */
import type { EntityId } from '@settlers/shared';
import { asEntityId } from '@settlers/shared';
import type { ComponentType } from './component.js';
import type { ComponentId } from './component.js';
import { ComponentStore } from './store.js';

export class World {
  private nextEntity = 1;
  private readonly alive = new Set<EntityId>();
  private readonly stores = new Map<ComponentId, ComponentStore<unknown>>();

  /** Allocate a new, empty entity. */
  create(): EntityId {
    const id = asEntityId(this.nextEntity++);
    this.alive.add(id);
    return id;
  }

  isAlive(entity: EntityId): boolean {
    return this.alive.has(entity);
  }

  /** Remove an entity and all of its components. */
  destroy(entity: EntityId): void {
    if (!this.alive.delete(entity)) return;
    for (const store of this.stores.values()) {
      store.remove(entity);
    }
  }

  entityCount(): number {
    return this.alive.size;
  }

  private storeFor<T>(type: ComponentType<T>): ComponentStore<T> {
    let store = this.stores.get(type.id);
    if (!store) {
      store = new ComponentStore<unknown>();
      this.stores.set(type.id, store);
    }
    // The store for `type.id` only ever holds `T` — the map is heterogeneous by
    // ComponentId, so this cast is the single, localised type boundary.
    return store as ComponentStore<T>;
  }

  add<T>(entity: EntityId, type: ComponentType<T>, value: T): void {
    this.storeFor(type).set(entity, value);
  }

  get<T>(entity: EntityId, type: ComponentType<T>): T | undefined {
    return this.storeFor(type).get(entity);
  }

  has<T>(entity: EntityId, type: ComponentType<T>): boolean {
    return this.storeFor(type).has(entity);
  }

  remove<T>(entity: EntityId, type: ComponentType<T>): void {
    this.storeFor(type).remove(entity);
  }

  /**
   * Entities possessing *all* of the given component types, in ascending id
   * order (deterministic). Iterates the smallest store for efficiency.
   */
  query(...types: readonly ComponentType<unknown>[]): EntityId[] {
    if (types.length === 0) return [];
    let smallest = this.storeFor(types[0] as ComponentType<unknown>);
    for (let i = 1; i < types.length; i++) {
      const s = this.storeFor(types[i] as ComponentType<unknown>);
      if (s.size < smallest.size) smallest = s;
    }
    const result: EntityId[] = [];
    outer: for (const entity of smallest.entities()) {
      for (const type of types) {
        if (!this.storeFor(type).has(entity)) continue outer;
      }
      result.push(entity);
    }
    result.sort((a, b) => a - b);
    return result;
  }
}
